import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Send, Trash2, Bot } from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { api, extractApiError } from "../lib/api";
import { fetchEntities } from "../lib/dataFetchers";
import type { AgentResponse, ChatMessage, EntityChip } from "../lib/types";
import { CHAT_HISTORY_KEY as STORAGE_KEY } from "../lib/chatHistory";
import { cn } from "../lib/cn";
import { TypingDots } from "../components/TypingDots";
import { Button } from "../components/ui/Button";

interface UiMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  chips?: EntityChip[];
}

interface StoredConversation {
  id: string;
  title: string;
  messages: UiMessage[];
  updatedAt: string;
}

// STORAGE_KEY is imported from chatHistory.ts above — single source of truth.

function loadConversations(): StoredConversation[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as StoredConversation[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveConversations(list: StoredConversation[]): void {
  if (typeof window === "undefined") return;
  try {
    const trimmed = list
      .map((c) => ({ ...c, messages: c.messages.slice(-50) }))
      .slice(-25);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    /* ignore quota */
  }
}

// Count-free stored fallback. The live entity count is injected at DISPLAY
// time via the "seed_message" i18n key, so no stale number is ever persisted.
const SEED: UiMessage[] = [
  {
    id: "seed",
    role: "assistant",
    content:
      "Hi! Ask anything about Hightekers' entities — renewals, UBO, directors, addresses. I cite the entities I used.",
  },
];

const SUGGESTIONS = [
  "Which entities have a licence expiring in 30 days?",
  "Who is the UBO of HIGHTEKERS Sàrl?",
  "Show LATAM entities with missing director.",
  "Which countries have multiple entities?",
];

export default function Chat(): JSX.Element {
  const { t } = useTranslation("chat");
  // Live entity count — shared cache with Home (["entities-all"]); stays correct
  // on entity create/delete/restore (those mutations invalidate this key).
  const { data: entitiesRes } = useQuery({
    queryKey: ["entities-all"],
    queryFn: () => fetchEntities({ limit: 100 }),
    staleTime: 60_000,
  });
  const entityCount = entitiesRes?.total ?? entitiesRes?.entities.length;
  const [conversations, setConversations] = useState<StoredConversation[]>(() => loadConversations());
  const [activeId, setActiveId] = useState<string | null>(() => {
    const list = loadConversations();
    return list[0]?.id ?? null;
  });
  const [messages, setMessages] = useState<UiMessage[]>(() => {
    const list = loadConversations();
    const first = list[0];
    return first?.messages ?? SEED;
  });
  const hasUserMessage = messages.some((m) => m.role === "user");
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!activeId) return;
    setConversations((prev) => {
      const next = prev.map((c) =>
        c.id === activeId ? { ...c, messages, updatedAt: new Date().toISOString() } : c,
      );
      saveConversations(next);
      return next;
    });
  }, [messages, activeId]);

  const newConversation = useCallback(() => {
    const id = `c-${Date.now()}`;
    const next: StoredConversation = {
      id,
      title: t("new_conversation"),
      messages: SEED,
      updatedAt: new Date().toISOString(),
    };
    setConversations((prev) => {
      const updated = [next, ...prev];
      saveConversations(updated);
      return updated;
    });
    setActiveId(id);
    setMessages(SEED);
  }, [t]);

  const deleteConversation = useCallback(
    (id: string) => {
      setConversations((prev) => {
        const updated = prev.filter((c) => c.id !== id);
        saveConversations(updated);
        return updated;
      });
      if (id === activeId) {
        setActiveId(null);
        setMessages(SEED);
      }
    },
    [activeId],
  );

  const select = useCallback((id: string) => {
    setActiveId(id);
    const conv = loadConversations().find((c) => c.id === id);
    setMessages(conv?.messages ?? SEED);
  }, []);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || busy) return;
      // Create conversation lazily.
      let convId = activeId;
      if (!convId) {
        convId = `c-${Date.now()}`;
        const next: StoredConversation = {
          id: convId,
          title: trimmed.slice(0, 40),
          messages: SEED,
          updatedAt: new Date().toISOString(),
        };
        setConversations((prev) => {
          const updated = [next, ...prev];
          saveConversations(updated);
          return updated;
        });
        setActiveId(convId);
      }

      const userMsg: UiMessage = { id: `u-${Date.now()}`, role: "user", content: trimmed };
      setMessages((m) => [...m, userMsg]);
      setInput("");
      setBusy(true);
      try {
        const wire: ChatMessage[] = [...messages, userMsg]
          .filter((m) => m.role === "user" || m.role === "assistant")
          .map((m) => ({ role: m.role, content: m.content }));
        const res = await api.post<AgentResponse>("/agent", { messages: wire });
        setMessages((m) => [
          ...m,
          {
            id: `a-${Date.now()}`,
            role: "assistant",
            content: res.data.answerText,
            chips: res.data.entities,
          },
        ]);
      } catch (err) {
        const ax = err as import("axios").AxiosError;
        if (ax.response?.status === 429) {
          const retryHeader = ax.response.headers["retry-after"] as string | undefined;
          const seconds = typeof retryHeader === "string" ? parseInt(retryHeader, 10) : NaN;
          const n = isNaN(seconds) ? 30 : seconds;
          toast.error(t("rate_limited", { seconds: n }));
        } else {
          toast.error(extractApiError(err));
        }
        setMessages((m) => [
          ...m,
          {
            id: `a-${Date.now()}`,
            role: "assistant",
            content: t("agent_unavailable"),
          },
        ]);
      } finally {
        setBusy(false);
        requestAnimationFrame(() => {
          scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight });
        });
      }
    },
    [busy, activeId, messages],
  );

  return (
    <div className="max-w-6xl mx-auto h-[calc(100vh-7rem)] flex flex-col">
      {/* Page header */}
      <div className="mb-3 sm:mb-4 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-[24px] leading-[1.1] font-display font-semibold text-text">
            {t("page_title")}
          </h1>
          {entityCount !== undefined && (
            <p className="text-[13px] text-muted mt-[5px]">
              {t("page_subtitle", { count: entityCount })}
            </p>
          )}
        </div>
        <Button variant="primary" onClick={newConversation}>
          <Plus className="h-4 w-4" aria-hidden />
          {t("new_button")}
        </Button>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[260px,1fr] gap-3 sm:gap-4">
        {/* Conversation history sidebar — proto .chat-h-ish look */}
        <aside
          className="bg-surface border border-border rounded-[11px] shadow-card overflow-y-auto max-h-[40vh] md:max-h-none flex flex-col"
        >
          {/* Sidebar header */}
          <div className="flex items-center gap-[9px] px-[15px] py-[13px] border-b border-border-2 shrink-0">
            <div className="w-7 h-7 rounded-[8px] bg-accent-soft text-accent flex items-center justify-center shrink-0">
              <Bot className="w-4 h-4" aria-hidden />
            </div>
            <span className="text-[10px] font-bold tracking-[.06em] uppercase text-dim">
              {t("history_label")}
            </span>
          </div>

          {/* Conversation list */}
          <div className="flex-1 overflow-auto p-[10px]">
            {conversations.length === 0 ? (
              <p className="px-2 py-3 text-[12px] text-muted">{t("no_history")}</p>
            ) : (
              <ul className="space-y-[3px]">
                {conversations.map((c) => (
                  <li key={c.id} className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => select(c.id)}
                      className={cn(
                        "flex-1 text-left px-[11px] rounded-[8px] text-[13px] transition-colors",
                        "min-h-[44px] sm:min-h-[36px] flex flex-col justify-center",
                        activeId === c.id
                          ? "bg-accent-soft text-accent"
                          : "hover:bg-surface-2 text-text",
                      )}
                    >
                      <span className={cn("block font-medium truncate", activeId === c.id && "font-semibold")}>
                        {c.title}
                      </span>
                      <span className="block text-[10.5px] text-dim">
                        {new Date(c.updatedAt).toLocaleString()}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteConversation(c.id)}
                      aria-label={t("delete_conversation", { title: c.title })}
                      className="h-[36px] w-[36px] inline-flex items-center justify-center rounded-[8px] text-dim hover:text-red hover:bg-red-soft transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>

        {/* Active chat panel — proto .chat look */}
        <div className="bg-surface border border-border rounded-[11px] shadow-card flex flex-col min-h-0">
          {/* Messages scroller — proto .chat-body */}
          <div
            ref={scrollerRef}
            className="flex-1 overflow-y-auto p-[15px] space-y-3"
          >
            {messages.map((m) =>
              m.role === "user" ? (
                <div key={m.id} className="flex justify-end">
                  <div
                    className="max-w-[85%] bg-accent text-white text-[13px] leading-[1.55] rounded-[12px] rounded-tr-[4px] px-[14px] py-[12px]"
                  >
                    {m.content}
                  </div>
                </div>
              ) : (
                <div key={m.id} className="flex justify-start">
                  <div className="max-w-[90%]">
                    {/* proto .bubble */}
                    <div
                      className="bg-surface-2 border border-border-2 rounded-[12px] rounded-tl-[4px] px-[14px] py-[12px] text-[12.5px] leading-[1.55] whitespace-pre-wrap"
                    >
                      {m.id === "seed"
                        ? t("seed_message", { count: entityCount ?? "" })
                        : m.content}
                    </div>
                    {m.chips && m.chips.length > 0 && (
                      <div className="mt-2">
                        <div className="text-[10px] font-bold tracking-[.06em] uppercase text-dim mb-[9px]">
                          {t("referenced_entities")}
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {m.chips.map((c) => (
                            <a
                              key={c.id}
                              href={`/entities/${c.id}`}
                              className="flex items-center gap-1 text-[12px] px-[9px] py-[3px] rounded-full bg-accent-soft text-accent border border-[#c7d2fe] font-medium hover:bg-accent-tint"
                            >
                              {c.flag && <span aria-hidden>{c.flag}</span>}
                              <span>{c.name}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ),
            )}
            {busy && <TypingDots />}
          </div>

          {/* Suggested prompts — proto .try look */}
          {!hasUserMessage && (
            <div className="border-t border-border-2 px-[15px] py-[12px]">
              <p className="text-[10px] font-bold tracking-[.06em] uppercase text-dim mb-[9px]">
                {t("history_label")}
              </p>
              <div className="flex flex-col gap-[7px]">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => void send(s)}
                    disabled={busy}
                    className="text-left bg-surface border border-border rounded-[9px] px-[12px] py-[9px] text-[12px] text-text hover:border-accent hover:bg-accent-tint disabled:opacity-50 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input footer — proto .chat-foot / .chat-input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void send(input);
            }}
            className="border-t border-border-2 p-[12px]"
          >
            <div className="flex items-center gap-[7px] border border-border rounded-[10px] px-[12px] py-[6px] bg-surface-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                type="text"
                placeholder={t("input_placeholder")}
                aria-label={t("input_placeholder")}
                autoComplete="off"
                className="flex-1 border-none bg-transparent text-[13px] text-text outline-none placeholder:text-dim"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                aria-label={t("send_label")}
                className="w-8 h-8 rounded-[8px] bg-accent text-white border-none flex items-center justify-center hover:bg-[#4338ca] disabled:opacity-50 transition-colors shrink-0"
              >
                <Send className="h-4 w-4" aria-hidden />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
